import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  AbstractEntityApiService,
  ApiService,
  ConfBaseEntityApiService,
  EndpointBuilder,
} from '@corezilla/api';
import { User } from '@corezilla/api/features/auth/interfaces/user';
import { UserRoleType } from '@corezilla/api/features/auth/interfaces/user-role-type';

/** Payload of an invitation — no password: Keycloak owns the credential. */
export interface UserInvitation {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  roles: UserRoleType[];
}

/**
 * Result of an invitation. `invitationMailSent` is false when the account could already
 * authenticate (existing Keycloak password or an external identity provider) — access was still
 * granted, but no set-password mail was needed.
 */
export interface InvitedUser extends User {
  invitationMailSent: boolean;
  keycloakUserCreated: boolean;
  keycloakRealmRole: string;
}

/**
 * Where an account stands between "invited" and "using Flowzilla". Read live from Keycloak, which
 * owns the credential — nothing about it is stored in the Configurator database.
 */
export interface InvitationStatus {
  hasKeycloakAccount: boolean;
  federated: boolean;
  canSignIn: boolean;
  invitationPending: boolean;
}

@Injectable({ providedIn: 'root' })
export class UsersApi extends AbstractEntityApiService<User> {
  /** Reuse the configuration-scoped base entity API for CRUD plumbing. */
  protected override readonly api = inject(ConfBaseEntityApiService<User>);

  /** Entity collection path handled by the backend. */
  override endpoint = 'users';

  /** Raw HTTP access for the invitation actions, which are not plain CRUD. */
  private readonly http = inject(ApiService);

  private get usersUrl(): string {
    return EndpointBuilder.toUrl([this.http.confUrl, this.endpoint]);
  }

  /**
   * Invite a person by e-mail. The backend creates them in Keycloak, adds them to the required
   * organization, provisions the local + Core account and triggers the set-password mail.
   */
  invite(invitation: UserInvitation): Observable<InvitedUser> {
    const url = EndpointBuilder.toUrl([this.usersUrl, 'invite']);

    return this.http
      .post<InvitedUser>(url, invitation)
      .pipe(map(response => response as InvitedUser));
  }

  /** Whether this user still has an outstanding invitation. */
  invitationStatus(userId: number): Observable<InvitationStatus> {
    const url = EndpointBuilder.toUrl([
      this.usersUrl,
      userId.toString(),
      'invitation-status',
    ]);

    return this.http
      .get<InvitationStatus>(url)
      .pipe(map(response => response as InvitationStatus));
  }

  /** Re-send the invitation mail for a user who has not signed in yet. */
  resendInvitation(userId: number): Observable<InvitedUser> {
    const url = EndpointBuilder.toUrl([
      this.usersUrl,
      userId.toString(),
      'resend-invitation',
    ]);

    return this.http
      .post<InvitedUser>(url, {})
      .pipe(map(response => response as InvitedUser));
  }
}
